import { ExternalServiceId, InternalId, StaticReference, StaticRevisionReference } from '@edgebox/data-definition-kit';
import {
  ClientContentItemEntity,
  ClientContentOptimizationDraft,
  ClientContentOptimizationEntity,
  ClientContentOptimizationTypeEntity,
  ClientContentRecommendationEntity,
  IContentRecommendationListFilters,
  ISyncCoreApi,
} from '@edgebox/sync-core-rest-client';
import {
  ContentOptimizationStatus,
  ContentOptimizationTargetStage,
  ContentRecommendationKind,
  ContentRecommendationStatus,
  ContentRepresentation,
  ISyncCoreContentRecommendation,
  ISyncCoreSite,
} from '@edgebox/sync-core-data-definitions';
import { IResolvedSuggestionTerm, SuggestedFaqRowState, sortSuggestedFaqs, toSettledRowState } from './SuggestedFaqs';
import {
  collectDependsOn,
  collectPublishOnPageKeys,
  collectSourcePageKeys,
  collectTermKeys,
  mapWithConcurrency,
  SuggestionDependencyReference,
} from './suggestedFaqs.utils';
import { loadTaxonomyTerm, readOncePerKey, readOncePerKeyCaches } from './taxonomyTermText.client';

/**
 * What the FAQ tab reads and writes: the page's open suggestions, the
 * optimizations it already carries, the names its badges show, and the two
 * actions a row offers.
 */

/** The optimization type a suggested FAQ is added to the page as. */
const FAQ_SECTION_OPTIMIZATION_TYPE_KEY = 'faq-section';
const SUGGESTED_FAQ_PAGE_SIZE = 100;
const SUGGESTED_FAQ_PAGE_LIMIT = 20;
const OPTIMIZATION_PAGE_SIZE = 100;
const OPTIMIZATION_PAGE_LIMIT = 20;
/** At most this many dependency reads are in flight at once. */
const DEPENDENCY_READ_CONCURRENCY = 6;
/** At most this many content-item reads are in flight at once. */
const CONTENT_ITEM_READ_CONCURRENCY = 6;

const readSuggestionQuestionOncePerKey = readOncePerKey<IResolvedSuggestionTerm>({
  caches: readOncePerKeyCaches<IResolvedSuggestionTerm>(),
});

export interface IFaqOptimizationContext {
  page: ClientContentItemEntity;
  site?: ISyncCoreSite;
  optimizationType?: ClientContentOptimizationTypeEntity;
}

/**
 * The filters one read of this page's suggestions narrows by: the page by its
 * stable key, the FAQ kind and the open status. The service declares them in the
 * domain package's own enums, so the values this tab already works with travel as
 * they are, and the results page travels beside them as the paging argument.
 */
function suggestedFaqListFilters(pageKey: string): IContentRecommendationListFilters {
  return {
    targetContentItemKey: pageKey,
    kind: ContentRecommendationKind.Faq,
    status: ContentRecommendationStatus.Open,
  };
}

/**
 * Every open FAQ suggestion of this page, paged to the end: a page with more
 * suggestions than one listing holds would otherwise show only the first of
 * them, and the count on the tab would disagree with the list under it.
 */
async function loadAllSuggestedFaqs(
  api: ISyncCoreApi,
  pageKey: string
): Promise<{ suggestions: ClientContentRecommendationEntity[]; capped: boolean }> {
  const collected: ClientContentRecommendationEntity[] = [];
  let capped = false;

  for (let page = 0; page < SUGGESTED_FAQ_PAGE_LIMIT; page++) {
    const listed = await api.governance.contentRecommendations.list(
      { page, itemsPerPage: SUGGESTED_FAQ_PAGE_SIZE },
      suggestedFaqListFilters(pageKey)
    );

    collected.push(...(listed?.items ?? []));
    const pages = listed?.numberOfPages ?? 1;
    if (page + 1 >= pages) {
      break;
    }

    // The page has more than this screen reads in one go, and the list below
    // says so rather than ending without a word.
    if (page + 1 >= SUGGESTED_FAQ_PAGE_LIMIT) {
      capped = true;
    }
  }

  return { suggestions: collected, capped };
}

/**
 * Every optimization of this page, paged to the end: a page with more
 * optimizations than one listing holds would otherwise offer to add a
 * suggestion it already carries, and would be taken for a page without a draft.
 *
 * A read that fails partway answers `partial`, because what was collected no
 * longer decides what the page carries: a row that was added would show its Add
 * action again, and using it twice writes a second draft, and a draft the unread
 * optimizations wrote would be missed.
 */
export async function loadPageOptimizations(
  api: ISyncCoreApi,
  pageKey: string
): Promise<{ items: ClientContentOptimizationEntity[]; partial: boolean }> {
  const collected: ClientContentOptimizationEntity[] = [];

  for (let page = 0; page < OPTIMIZATION_PAGE_LIMIT; page++) {
    const listed = await api.governance.contentOptimizations
      .list({ page, itemsPerPage: OPTIMIZATION_PAGE_SIZE }, { inputPageKey: pageKey })
      .catch(() => undefined);

    if (!listed) {
      return { items: collected, partial: true };
    }

    collected.push(...(listed.items || []));
    if (page + 1 >= (listed.numberOfPages ?? 1)) {
      return { items: collected, partial: false };
    }
  }

  // The page carries more optimizations than this screen reads in one go, so what
  // it already has was not read to the end either.
  return { items: collected, partial: true };
}

export interface ILoadedSuggestedFaqs {
  suggestions: ClientContentRecommendationEntity[];
  /** True when the page carries more suggestions than this screen reads in one go. */
  capped: boolean;
  /**
   * True when this page's optimizations could not all be read, so which suggestions
   * it already carries is not known for certain.
   */
  addedStateIncomplete: boolean;
  states: Record<string, SuggestedFaqRowState>;
  optimizationContext: IFaqOptimizationContext;
}

export async function loadSuggestedFaqs(api: ISyncCoreApi, page: ClientContentItemEntity): Promise<ILoadedSuggestedFaqs> {
  const pageKey = `${page.key}`;

  const [listed, optimizations, optimizationType, site] = await Promise.all([
    loadAllSuggestedFaqs(api, pageKey),
    loadPageOptimizations(api, pageKey),
    api.governance.contentOptimizationTypes.itemByKey(FAQ_SECTION_OPTIMIZATION_TYPE_KEY as ExternalServiceId).catch(() => undefined),
    page.site ? page.site.get().catch(() => undefined) : Promise.resolve(undefined),
  ]);

  // A suggestion this page already has an optimization for reads as added.
  const addedIds = new Set<string>();
  for (const optimization of optimizations?.items ?? []) {
    for (const reference of optimization.fixesRecommendations ?? []) {
      const id = reference?.getId?.();
      if (id) {
        addedIds.add(`${id}`);
      }
    }
  }

  const suggestions = sortSuggestedFaqs(listed.suggestions);
  const states: Record<string, SuggestedFaqRowState> = {};
  for (const suggestion of suggestions) {
    const settled = toSettledRowState(suggestion.status);
    states[suggestion.id] = settled === 'open' && addedIds.has(suggestion.id) ? 'added' : settled;
  }

  return {
    suggestions,
    capped: listed.capped,
    addedStateIncomplete: optimizations.partial,
    states,
    optimizationContext: { page, site: site as ISyncCoreSite | undefined, optimizationType: optimizationType ?? undefined },
  };
}

/**
 * What a suggestion waits on is another suggestion, not a taxonomy term, so it is
 * named by its own question, read through the reference the suggestion carries.
 * `getRevision` resolves the revision the reference names through the service the
 * client registered for this entity; `get` would ask for the newest revision by
 * key, which that registration does not answer.
 *
 * It is read once per stable key and kept for the session the way a taxonomy term
 * is: reopening the tab re-uses what was already read.
 */
async function loadSuggestionQuestion({
  key,
  reference,
}: {
  key: string;
  reference: SuggestionDependencyReference;
}): Promise<IResolvedSuggestionTerm | undefined> {
  return readSuggestionQuestionOncePerKey({
    key,
    load: async () => {
      const dependency = await reference.getRevision();
      const name = dependency?.texts?.[0]?.title?.text?.trim();
      return name ? { name } : undefined;
    },
  });
}

export async function loadSuggestedFaqTerms(
  api: ISyncCoreApi,
  suggestions: ClientContentRecommendationEntity[]
): Promise<Record<string, IResolvedSuggestionTerm>> {
  const resolved: Record<string, IResolvedSuggestionTerm> = {};

  await Promise.all([
    ...collectTermKeys(suggestions).map(async (termKey) => {
      const term = await loadTaxonomyTerm(api, termKey);
      if (term) {
        resolved[termKey] = term;
      }
    }),
    // The page a suggestion says to publish on and the pages it can be answered
    // from are content items alike, so they are read once per key and a few at a
    // time, the way the dependency questions are: a page carrying a dozen
    // sources cannot put a dozen requests on the wire at once.
    mapWithConcurrency(
      [...new Set([...collectPublishOnPageKeys(suggestions), ...collectSourcePageKeys(suggestions)])],
      CONTENT_ITEM_READ_CONCURRENCY,
      async (pageKey) => {
        const page = await api.governance.contentItems.itemByKey(pageKey as ExternalServiceId).catch(() => undefined);
        const name = page?.name?.trim();
        if (name) {
          // A page is addressed only where the record carries an address, so a
          // page without one is named and not linked.
          resolved[pageKey] = { name, url: page?.url?.trim() || undefined };
        }
      }
    ),
    mapWithConcurrency(collectDependsOn(suggestions), DEPENDENCY_READ_CONCURRENCY, async ({ key, reference }) => {
      const dependency = await loadSuggestionQuestion({ key, reference });
      if (dependency) {
        resolved[key] = dependency;
      }
    }),
  ]);

  return resolved;
}

/**
 * Creates the content-optimization draft for this page. The create route is the
 * one place that decides whether the site's own optimizer runs or ours does, so
 * nothing is evaluated here.
 */
export async function createFaqSectionOptimization(
  api: ISyncCoreApi,
  context: IFaqOptimizationContext | undefined,
  suggestion: ClientContentRecommendationEntity
): Promise<{ taskGroupId?: string }> {
  // A page whose optimization type or site cannot be read cannot be optimized;
  // the row says so in the tab's own words and keeps both of its actions.
  if (!context?.optimizationType || !context.site) {
    throw new Error("Couldn't add this FAQ to the page.");
  }

  const draft = new ClientContentOptimizationDraft();
  draft.targetStage = ContentOptimizationTargetStage.Published;
  draft.status = ContentOptimizationStatus.Generating;
  draft.site = new StaticReference(context.site);
  draft.types = [new StaticRevisionReference(context.optimizationType)];
  draft.fixesIssues = [];
  draft.fixesIssueTypes = [];

  // A content item is a page or a stand-alone entity, and the create route
  // carries each one on its own property, the way this area's other draft
  // builders do.
  if (context.page.type === ContentRepresentation.Page) {
    draft.inputPage = new StaticRevisionReference(context.page);
  } else {
    draft.inputEntity = new StaticRevisionReference(context.page);
  }
  // The suggestion travels by id; the draft names the domain entity the client
  // entity implements, and only the id crosses.
  draft.fixesRecommendations = [new StaticReference(suggestion as ISyncCoreContentRecommendation)];

  // The create answers the task group that writes the draft; generation itself
  // is a task, so the page follows that group until it ends.
  const response = await api.governance.contentOptimizations.create(draft);
  return { taskGroupId: response?.taskGroup?.id ? `${response.taskGroup.id}` : undefined };
}

export async function dismissContentRecommendation(api: ISyncCoreApi, id: InternalId): Promise<void> {
  await api.governance.contentRecommendations.patchStatus(id, { status: ContentRecommendationStatus.Ignored });
}
